// OrderView.jsx
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

const formatCpfCnpj = (value) => {
  if (!value) return "-";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  } else if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return value;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return dateStr.split("T")[0].split('-').reverse().join('/');
};

const formatCurrency = (value) => {
  if (typeof value !== "number") return "-";
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const statusConfig = {
  Orcamento: {
    color: "bg-gray-400",
    title: "Orçamento",
    description: "Orçamento aguardando transformação em Pedido",
  },
  Aguardando: {
    color: "bg-yellow-400",
    title: "Aguardando",
    description: "Pedido em espera para processamento inicial",
  },
  Análise: {
    color: "bg-purple-400",
    title: "Análise",
    description: "Pedido em análise pela equipe técnica",
  },
  Carteira: {
    color: "bg-blue-400",
    title: "Carteira",
    description: "Pedido em carteira para processamento",
  },
  Faturado: {
    color: "bg-green-400",
    title: "Faturado",
    description: "Pedido faturado e finalizado",
  },
  Reprovado: {
    color: "bg-red-400",
    title: "Reprovado",
    description: "Pedido reprovado ou cancelado",
  },
};

const OrderView = ({ orderId, onClose, onEdit }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/orders/${orderId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!response.ok) {
          throw new Error("Erro ao buscar detalhes do pedido.");
        }
        const data = await response.json();
        setOrder(data);
      } catch (err) {
        setError("Erro ao carregar os detalhes do pedido. Por favor, tente novamente.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrderDetails();
  }, [orderId]);

  const printOrder = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/orders/${orderId}/pdf`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!response.ok) {
        throw new Error("Erro ao gerar PDF do pedido.");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pedido-${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar PDF:", error);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 no-print">
        <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-sm">
          <p className="text-gray-600">Carregando detalhes do pedido...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 no-print">
        <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-sm">
          <p className="text-red-600">{error}</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
            Fechar
          </button>
        </div>
      </div>
    );
  }

  const mappedStatus = order.status;
  const statusData = statusConfig[mappedStatus];

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 no-print">
        <div className="relative top-6 mx-auto w-full max-w-6xl mb-6 px-4">
          <div className="bg-white shadow-lg rounded-lg">
            <div className="flex md:flex-row items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-700">Detalhes do Pedido</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 md:mt-0">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Informações Básicas</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Vendedor</label>
                      <p className="mt-1">
                      {order.seller} - {order.sellerName}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Cliente</label>
                      <p className="mt-1">
                        {order.customerName} ({formatCpfCnpj(order.customerDocument)})
                      </p>
                    </div>
                    <div className="">
                      <label className="block text-sm font-medium text-gray-500">Identificador do Pedido</label>
                      <p className="mt-1">#{order.id}</p>
                    </div>
                    <div className="">
                      <label className="block text-sm font-medium text-gray-500">Data Emissão</label>
                      <p className="mt-1">{formatDate(order.createdAt)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Data Agendada</label>
                      <p className="mt-1">{formatDate(order.scheduleDate)}</p>
                    </div>
                    <div className="">
                      <label className="block text-sm font-medium text-gray-500">Forma de Pagamento</label>
                      <p className="mt-1">{order.paymentMethod === "cash" ? "À Vista" : "A Prazo"} | {order.descPaymentCondition} </p>
                    </div>
                    {order.cashDiscount > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Desconto à Vista</label>
                        <p className="mt-1">{order.cashDiscount ? `${order.cashDiscount}%` : '0%'}</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Status</label>
                      <div className="mt-1 items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full text-white ${statusData.color}`}>
                          {statusData.title}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Itens do Pedido</h3>
                <div className="rounded-lg p-4">
                  <div className="hidden md:block">
                    <table className="w-full border rounded-lg">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Imagem</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantidade</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Preço Unitário</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody className="bg-gray-50">
                        {order.items.map((item, index) => (
                          <tr key={index} className="border-t">
                            <td className="px-4 py-3">
                              {item.image ? (
                                <img src={item.image} alt={item.product} className="w-16 h-16 object-cover rounded" />
                              ) : (
                                <div className="w-16 h-16 bg-gray-200 rounded" />
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {item.code} - {item.product}
                            </td>
                            <td className="px-4 py-3">{item.quantity}</td>
                            <td className="px-4 py-3">{formatCurrency(item.price)}</td>
                            <td className="px-4 py-3">{formatCurrency(item.subtotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="block md:hidden space-y-4">
                    {order.items.map((item, index) => (
                      <div key={index} className="border rounded-lg p-4 shadow-sm">
                        <div className="flex items-center">
                          {item.image ? (
                            <img src={item.image} alt={item.product} className="w-16 h-16 object-cover rounded" />
                          ) : (
                            <div className="w-16 h-16 bg-gray-200 rounded" />
                          )}
                          <div className="ml-3">
                            <p className="font-medium text-sm">{item.product}</p>
                            <p className="text-xs text-gray-500">{item.code}</p>
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-600">Quantidade: {item.quantity}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Preço: {formatCurrency(item.price)}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-gray-600">Subtotal: {formatCurrency(item.subtotal)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Totais do Pedido</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-2 text-right">
                    <p className="text-sm text-gray-500">Subtotal: {formatCurrency(order.subtotal)}</p>
                    <p className="text-sm text-gray-500">Descontos: {formatCurrency(order.totalDiscount)}</p>
                    <p className="text-xl font-bold text-red-600">Total: {formatCurrency(order.total)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t flex justify-end p-6 no-print">
              <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 mr-2">
                Fechar
              </button>
              {order.status === "Orcamento" && (
                <button
                  onClick={() => onEdit(order)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 mr-2"
                >
                  Editar
                </button>
              )}
              <button onClick={printOrder} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                Imprimir
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderView;
